/**
 * Does anybody manage to install this? F-123, moved here from CloudWatch by F-125.
 *
 * Leo, 2026-09-16: *"move it into the admin and get rid of cloudwatch metrics for this, we
 * dont need alerts."* The counts were nine custom CloudWatch metrics behind a dashboard, at
 * $3.00 a month against a $30 budget and expiring at fifteen months; they are now rows in
 * `nabu-main` with no retention limit and no per-metric charge.
 *
 * **The placement is most of the value.** F-123 rejected DynamoDB counters for one reason —
 * that nothing could read them without an operator console — and there was one an hour later.
 * A dashboard in another AWS console is a number nobody finds; these sit one click from the
 * learners they are about to account for.
 *
 * ---
 *
 * ### The caveats are part of the screen, not a document somewhere
 *
 * They were eighty lines across the top of the dashboard that F-125 deleted, and they are the
 * half worth keeping. The decision resting on these numbers is whether to build a native app —
 * expensive, permanent, and taken months from now by somebody reading this page — and a funnel
 * that overstates its certainty is worse than no funnel at all when that is the question.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useEffect, useState } from 'react';
import { getFunnel, isError, type AdminFunnel, type ApiError } from './api';
import { Failed, Waiting } from './parts';
import { iosInstallRate, percent, promptInstallRate } from './reach';

/** One row of the funnel table: a label, and the two routes it splits into. */
const ROWS: ReadonlyArray<{ label: string; note: string; pairs: [string, string][] }> = [
  {
    label: 'Offered',
    note: 'browsers shown the install card, once each, ever · counted by the page',
    pairs: [
      ['offered:ios-safari', 'iOS, in Safari'],
      ['offered:ios-other', 'iOS, another browser'],
      ['offered:prompt', 'a real install button'],
    ],
  },
  {
    label: 'Installed',
    note: 'first launch from the home screen, once per app, ever · counted by the page',
    pairs: [
      ['installed:ios', 'iOS'],
      ['installed:other', 'everything else'],
    ],
  },
  {
    label: 'Granted',
    note: 'browsers that granted notification permission · counted by the page',
    pairs: [
      ['granted:ios', 'iOS'],
      ['granted:other', 'everything else'],
    ],
  },
  {
    label: 'Reachable',
    note: 'devices that got a push subscription row · established by the API itself',
    pairs: [
      ['reachable:ios', 'iOS'],
      ['reachable:other', 'everything else'],
    ],
  },
];

function Counts({ funnel }: { funnel: AdminFunnel }) {
  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th>Stage</th>
          <th>Route</th>
          <th className="num">Count</th>
        </tr>
      </thead>
      <tbody>
        {ROWS.map((row) =>
          row.pairs.map(([key, route], index) => (
            <tr key={key}>
              {index === 0 && (
                <th scope="rowgroup" rowSpan={row.pairs.length}>
                  <span className="stack">
                    <span className="strong">{row.label}</span>
                    <span className="sub">{row.note}</span>
                  </span>
                </th>
              )}
              <td>{route}</td>
              <td className="num">{(funnel.totals[key] ?? 0).toLocaleString('en')}</td>
            </tr>
          )),
        )}
      </tbody>
    </table>
  );
}

export default function Funnel() {
  const [funnel, setFunnel] = useState<AdminFunnel>();
  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    let stale = false;
    void getFunnel().then((result) => {
      if (stale) return;
      if (isError(result)) setError(result);
      else setFunnel(result);
    });
    return () => {
      stale = true;
    };
  }, []);

  if (error) return <Failed error={error} what="Install funnel" />;
  if (!funnel) return <Waiting what="the install funnel" />;

  const ios = iosInstallRate(funnel.totals);
  const prompt = promptInstallRate(funnel.totals);

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h1>Installs</h1>
        <p className="sub">
          {funnel.since ? `cumulative since ${funnel.since}` : 'nothing counted yet'}
          {funnel.truncated && ' · older days not read'}
        </p>
      </header>

      <div className="funnel-rates">
        <span className="stack">
          <span className="num big">{percent(ios)}</span>
          <span className="sub">of iOS visitors offered the card end up installed</span>
        </span>
        <span className="stack">
          <span className="num big">{percent(prompt)}</span>
          <span className="sub">of visitors shown a real install button install it</span>
        </span>
      </div>

      {/* The caveats, above the numbers rather than below them, because a reader who stops
          after the figures must have read the thing that qualifies them. */}
      <div className="admin-note">
        <p>
          <strong>Chromium is exact.</strong> The offer, the install and the permission all
          happen in one browser profile, so that ratio is a ratio of the same people.
        </p>
        <p>
          <strong>iOS is a cohort rate, not an attribution.</strong> A home-screen app on iOS
          gets its own storage, separate from the Safari that installed it, so the app cannot
          recognise itself as the browser that was shown the card — and there is no install
          event on iOS at all, only a later standalone launch, possibly days later and quite
          possibly before any account exists. <code>installed:ios</code> over the two{' '}
          <code>offered:ios-*</code> counts is therefore a <em>ratio of two populations, not a
          path traced through one</em>. It cannot say which offer an install came from, it
          includes installs by people who never saw the card, and it misses anybody who
          installs and then carries on using the tab.
        </p>
        <p>
          <strong>Reachable is the only row the server establishes for itself</strong>, from the
          push endpoint: <code>web.push.apple.com</code> can only belong to an installed iOS
          app, because an iOS tab has no <code>PushManager</code>. If it runs ahead of Granted
          for long, the browser-side counting has stopped and the other three rows are
          understating reality.
        </p>
        <p>
          <strong>Read this over a season, not a week.</strong> At this traffic a week is a
          handful of events, and a handful of events read as a rate is noise somebody will
          nonetheless take for a trend.
        </p>
      </div>

      <div className="admin-scroll">
        <Counts funnel={funnel} />
      </div>

      {/*
       * The refusal counter, which since F-125 is the whole of the warning — the alarm that
       * used to email about it was removed at the owner's instruction. Anything but zero
       * means the browser and the API disagree about the funnel's vocabulary, or a learner
       * subscribed while their own report could not be sent; either way the four rows above
       * are too low, and "nobody installs it" is the wrong conclusion they invite.
       */}
      <p className={funnel.rejected > 0 ? 'admin-empty bad' : 'sub'}>
        {funnel.rejected > 0
          ? `${funnel.rejected} report(s) refused or never sent — the counts above are understating reality. Read install_funnel_rejected in /nabu/api.`
          : 'No refused or missing reports. Nothing alerts on this: it is a number to glance at, not a page.'}
      </p>

      {funnel.days.length > 0 && (
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Day (UTC)</th>
                {ROWS.flatMap((row) => row.pairs).map(([key]) => (
                  <th key={key} className="num">
                    {key}
                  </th>
                ))}
                <th className="num">refused</th>
              </tr>
            </thead>
            <tbody>
              {/* Newest first, as the API returns them. A change to the install offer shows
                  up here as a seam, which is the entire reason these are bucketed by day
                  rather than kept as one running total. */}
              {funnel.days.map((day) => (
                <tr key={day.day}>
                  <td className="num">{day.day}</td>
                  {ROWS.flatMap((row) => row.pairs).map(([key]) => (
                    <td key={key} className="num">
                      {day.counts[key] ?? ''}
                    </td>
                  ))}
                  <td className="num">{day.rejected || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
